import React, { useState } from "react";
import { Box, Button, CircularProgress, Container, TextField, Typography } from "@mui/material";
import { palette } from "homepage-new/palette";
import { baseURL } from "../../aws-config";
import api from "../../api";
import CallbackConfirmed from "./CallbackConfirmed";

const font = "Be Vietnam Pro";

// What a brand-new account sees straight after Google sign-in.
//
// This used to be RegistrationForm's thirteen fields, which contradicted the
// homepage promise ("no form, no funnel") at the moment someone first trusted
// us with their account. One field is a fair trade: we cannot call anybody
// without a number, and asking for it is what stops the candidate having to
// chase us. Everything else gets filled in later, with their tutor.
export const WelcomeFirst = () => {
  const [phone, setPhone] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  // phone_number is CharField(max_length=15) on the backend.
  const validate = (value) => {
    const trimmed = value.replace(/[\s-]/g, "");
    if (!trimmed) return "We need a number to call you on.";
    if (!/^\+?\d{10,15}$/.test(trimmed)) return "That doesn't look like a phone number.";
    if (trimmed.length > 15) return "That number is too long.";
    return "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const problem = validate(phone);
    if (problem) {
      setError(problem);
      return;
    }
    setSaving(true);
    setError("");
    try {
      // The serializer is partial and every other field is blank/defaulted, so
      // this alone is valid. The backend sets form_filled itself.
      await api.post(`${baseURL}user-profile/`, {
        phone_number: phone.replace(/[\s-]/g, ""),
      });
      setDone(true);
    } catch (err) {
      setError("We couldn't save that just now. Try again in a moment.");
    } finally {
      setSaving(false);
    }
  };

  if (done) return <CallbackConfirmed phone={phone} />;

  return (
    <Container maxWidth="sm">
      <Box sx={{ py: { xs: 6, md: 10 }, textAlign: "center" }}>
        <Typography
          component="h1"
          sx={{
            color: palette.text,
            fontWeight: 700,
            letterSpacing: "-0.03em",
            lineHeight: 1.15,
            fontSize: { xs: 29, md: 38 },
            fontFamily: font,
          }}
        >
          You're in. Now the actual first step.
        </Typography>

        <Typography
          component="p"
          sx={{
            color: palette.textMuted,
            fontSize: { xs: 16.5, md: 18 },
            lineHeight: 1.68,
            mt: 3,
            fontFamily: font,
          }}
        >
          We don't have a plan for you yet — that's the part we do together.
          Leave us a number and one of us will call you: half an hour, no cost
          and no card, to work out where you are and what it would really take
          to get where you're going.
        </Typography>

        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            mt: 4.5,
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            gap: 1.5,
            justifyContent: "center",
            alignItems: "flex-start",
          }}
        >
          <TextField
            value={phone}
            onChange={(e) => {
              setPhone(e.target.value);
              if (error) setError("");
            }}
            placeholder="Your phone number"
            type="tel"
            autoComplete="tel"
            inputProps={{ "aria-label": "Your phone number", maxLength: 16 }}
            error={Boolean(error)}
            helperText={error}
            sx={{
              flex: 1,
              maxWidth: { sm: 280 },
              "& .MuiOutlinedInput-root": { borderRadius: "999px" },
              "& .MuiInputBase-input": { fontFamily: font, py: 1.6, px: 2.5 },
              "& .MuiFormHelperText-root": { fontFamily: font, textAlign: "left", ml: 2 },
            }}
          />
          <Button
            type="submit"
            disabled={saving}
            disableElevation
            sx={{
              px: 3.5,
              py: 1.6,
              borderRadius: "999px",
              background: palette.ctaGradient,
              color: "#ffffff",
              fontSize: 16,
              fontWeight: 600,
              textTransform: "none",
              whiteSpace: "nowrap",
              fontFamily: font,
              "&:hover": { background: palette.primaryHover },
              "&.Mui-disabled": { background: palette.borderStrong, color: "#ffffff" },
            }}
          >
            {saving ? <CircularProgress size={22} color="inherit" /> : "Ask us to call you"}
          </Button>
        </Box>

        <Typography
          component="p"
          sx={{
            color: palette.textFaint,
            fontSize: 13.5,
            mt: 2.5,
            fontFamily: font,
          }}
        >
          Only used to reach you about your plan. Nothing else, ever.
        </Typography>
      </Box>
    </Container>
  );
};

export default WelcomeFirst;
